import { flow } from 'fp-ts/function';
import { filter, get, groupBy, identity, keys, map, sortBy } from 'lodash/fp';
import { array } from 'fp-ts';
import { createSelector } from 'reselect';
import { AccountingState, ServiceOrder, ServiceOrderType } from '@mews-commander/data-access';
import { OrderItem } from '@mews-commander/types';
import { getProp, getStringProp, ifElse, primitiveTypeUniq } from '@mews/fp';

import { composeProps, createPropsExtender, DateTimeService, ifProps } from '@mews/framework';

import { getFeatureFlagService } from '@mews-commander/legacy-app-instance';
import { FeatureFlag } from '@mews-commander/enums';
import { getService } from '@mews-commander/legacy-entities/src/ducks/services';
import { getOrderItemsByBillId, getOrderItemsByBillIds } from '@mews-commander/legacy-entities/src/ducks/orderItems';
import { getGeneralServiceOrderNoteByServiceOrderId } from '@mews-commander/legacy-entities/src/ducks/serviceOrderNotes';
import { getServiceOrdersByIds } from '@mews-commander/legacy-entities/src/ducks/serviceOrders';
import { createServiceTimeUnitLocalizationSelector } from '@mews-commander/legacy-entities/src/utils/serviceTimeUnitLocalization';
import { getServiceOrderServiceId } from '@mews-commander/legacy-entities/src/selectors/serviceOrders';
import { getProductServiceOrder } from '@mews-commander/legacy-entities/src/ducks/productServiceOrders';
import { getCurrentEnterpriseIanaTimeZoneIdentifier } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { isProductServiceOrder, isReservation } from '@mews-commander/legacy-entities/src/ducks/serviceOrderNew';

export const getServiceNames = createSelector(getService, (service) => get('names', service));

export const getOpenOrClosedOrderItemsByBillIds = createSelector(
    getOrderItemsByBillIds,
    filter<OrderItem>(
        (item) => item.accountingState === AccountingState.Open || item.accountingState === AccountingState.Closed
    )
);

export const getOpenOrClosedOrderItemsByBillId = createSelector(
    getOrderItemsByBillId,
    filter<OrderItem>(
        (item) => item.accountingState === AccountingState.Open || item.accountingState === AccountingState.Closed
    )
);

const getOpenOrClosedOrderItemsServiceIds = flow(getOpenOrClosedOrderItemsByBillIds, map('serviceOrderId'));

export const getProductServiceOrderIds = flow(
    composeProps(getServiceOrdersByIds, createPropsExtender('ids', getOpenOrClosedOrderItemsServiceIds)),
    filter<ServiceOrder>(['data.discriminator', ServiceOrderType.ProductServiceOrder]),
    map('id')
);

export const getServiceOrderNoteText = createSelector(getGeneralServiceOrderNoteByServiceOrderId, get('text'));

export const getProductServiceOrderAccountId = createSelector(getProductServiceOrder, (productServiceOrder) =>
    get('ownerId', productServiceOrder)
);

export const getServiceTimeUnitLocalizationByServiceOrderId = composeProps(
    ifProps(['id'], createServiceTimeUnitLocalizationSelector()),
    createPropsExtender('id', getServiceOrderServiceId)
);

export const getYearMonth = (dateString: string, timezone: string) => {
    const dateTime = DateTimeService.fromUtcString(dateString, timezone);
    return DateTimeService.toYearMonth(dateTime);
};

export const groupOrderItemsByMonth = (orderItems: OrderItem[], timezone: string) =>
    groupBy(({ consumedUtc }) => getYearMonth(consumedUtc, timezone), orderItems);

const getMonthlyGroupedOrderItems = createSelector(
    getOpenOrClosedOrderItemsByBillId,
    getCurrentEnterpriseIanaTimeZoneIdentifier,
    groupOrderItemsByMonth
);

export const getMonthOrdersServiceOrders = createSelector(getStringProp('month'), getMonthlyGroupedOrderItems, get);

const filterGetUngroupableOrderItems = array.filter((orderItem: OrderItem) => orderItem.displayGrouped === false);

const filterGetGroupableOrderItems = array.filter((orderItem: OrderItem) => orderItem.displayGrouped !== false);

export const getGroupableOrderItemsByBillId = createSelector(
    getOpenOrClosedOrderItemsByBillId,
    filterGetGroupableOrderItems
);

export const getUngroupableOrderItemsByBillId = flow(getOpenOrClosedOrderItemsByBillId, filterGetUngroupableOrderItems);

const isMonthSet = createSelector(getProp<string | undefined>()('month'), (month) => !!month);
export const getBillOrMonthOrderItems = ifElse(isMonthSet, getMonthOrdersServiceOrders, getGroupableOrderItemsByBillId);

export const getMonthlyOrdersServiceOrdersMonths = createSelector(
    getMonthlyGroupedOrderItems,
    flow(keys, sortBy(identity))
);

const getMonthOrdersServiceOrderIds = createSelector(
    getMonthOrdersServiceOrders,
    flow(map('serviceOrderId'), primitiveTypeUniq)
);
export const getOrderedMonthServiceOrders = composeProps(
    getServiceOrdersByIds,
    createPropsExtender('ids', getMonthOrdersServiceOrderIds)
);

export const getUnlinkedOrderedMonthServiceOrders = createSelector(
    getOrderedMonthServiceOrders,
    getFeatureFlagService,
    (serviceOrders, featureFlagService) => {
        const reservationIds = serviceOrders.filter((s) => isReservation(s)).map((s) => s.data.value.id);

        return featureFlagService.hasFlag(FeatureFlag.linkProductsToReservationsEnabled)
            ? serviceOrders.filter((serviceOrder) => {
                  const isProductServiceOrderType = isProductServiceOrder(serviceOrder);
                  const linkedReservationId = isProductServiceOrderType && serviceOrder.data.value.linkedReservationId;

                  return (
                      !isProductServiceOrderType ||
                      !linkedReservationId ||
                      !reservationIds.includes(linkedReservationId)
                  );
              })
            : serviceOrders;
    }
);
